import { Alert, Button, Checkbox, IconButton } from '@mui/joy';
import { type Dispatch, type SetStateAction, useContext } from 'react';
import { Form, useFormikContext } from 'formik';
import { toInteger } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  CancelIcon,
  InlineSpinner,
  NextIcon,
  PreviousIcon,
  ValidIcon,
  WarningIcon,
} from '@/shared/icons/Icons';
import { type FormikValues, useSteps } from './AddRepairCaseStepper';
import { actions } from '@/reducers/ui';
import { AppContext } from '@/shared/context/context';
import ConfirmationModal from '@/shared/components/ConfirmationModal';
import { ExcessType } from '~/common/enums';
import Summary from './Summary';
import useToggle from '@/shared/hooks/UseToggle';

type Props = {
  uiError: string | null;
  setUiError: (error: string | null) => void;
  setRedirect: Dispatch<
    SetStateAction<{
      toCase: boolean;
      toCaseInNewTab: boolean;
    }>
  >;
  isSubmitting: boolean;
  redirect: { toCase: boolean; toCaseInNewTab: boolean };
};

const AddRepairCaseFormBody: React.FC<Props> = ({
  uiError,
  setUiError,
  setRedirect,
  isSubmitting,
  redirect,
}) => {
  const { t } = useTranslation();
  const { ui, setUi, currentTeam } = useContext(AppContext);
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const { steps } = useSteps();

  const currentStep = ui.case.stepper.repairCase.step;
  const showClientAddressWarning = useToggle();

  const handleNext = async () => {
    switch (currentStep) {
      case 1: {
        const hasAddress =
          values.address.addressLine && values.address.city && values.address.postalCode;

        if (values.buildingId || hasAddress) {
          setUi({
            payload: currentStep + 1,
            type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
          });
          setUiError(null);
        } else {
          setUiError(t('repairCaseForm.address.error'));
        }

        break;
      }

      case 2:
        if (currentTeam?.allowRepairManagement && !values.contractId) {
          setUiError(t('repairCaseForm.excess.contract'));
          break;
        }

        if (values.excessType === ExcessType.None) {
          setUi({
            payload: currentStep + 1,
            type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
          });
          setUiError(null);
        }

        if (
          values.excessType === ExcessType.LegalExcess ||
          values.excessType === ExcessType.EnglishExcess
        ) {
          if (
            (toInteger(values.excessAmount) && toInteger(values.excessIncreasedAmount) === 0) ||
            toInteger(values.excessAmount) < toInteger(values.excessIncreasedAmount)
          ) {
            setUi({
              payload: currentStep + 1,
              type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
            });
            setUiError(null);
          } else if (toInteger(values.excessAmount) >= toInteger(values.excessIncreasedAmount)) {
            setUiError(t('repairCaseForm.excess.increasedExcessError'));
          } else {
            setUiError(t('repairCaseForm.excess.error'));
          }
        }
        break;
      case 4:
        {
          const hasDescription = values.description.length > 3;

          if (hasDescription) {
            setUi({
              payload: currentStep + 1,
              type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
            });
            setUiError(null);
          } else {
            setUiError(t('repairCaseForm.damage.error'));
          }
        }
        break;
      case 5:
        {
          const hasRequestor = values.relations.some(
            (relation: { isRequestor: boolean }) => relation.isRequestor,
          );
          const clients = values.relations.filter(
            (relation: { isClient: boolean }) => relation.isClient,
          );

          const isValidInvolvedParties = hasRequestor && clients.length > 0;

          if (isValidInvolvedParties) {
            setUiError(null);

            if (clients.some((client) => client.address == null || !client.address.addressLine)) {
              showClientAddressWarning.show();

              return;
            }

            setUi({
              payload: currentStep + 1,
              type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
            });
          } else {
            setUiError(t('repairCaseForm.involvedParties.error'));
          }
        }
        break;
      default:
        setUi({
          payload: currentStep + 1,
          type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
        });
        break;
    }
  };

  const handleCloseMissingClientAddress = () => {
    setUi({
      payload: currentStep + 1,
      type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
    });
  };

  const handleConfirmMissingClientAddress = () => {
    setFieldValue(
      'relations',
      values.relations.map((relation) => {
        if (
          relation.isClient &&
          (relation.address == null ||
            (!relation.address.addressLine &&
              !relation.address.city &&
              !relation.address.postalCode))
        ) {
          return {
            ...relation,
            address: {
              addressLine: values.address.addressLine,
              city: values.address.city,
              country: values.address.country,
              postalCode: values.address.postalCode,
            },
          };
        }
        return relation;
      }),
    );

    handleCloseMissingClientAddress();
  };

  return (
    <Form className="flex flex-col space-y-4">
      {uiError && (
        <Alert
          startDecorator={<WarningIcon />}
          color="danger"
          endDecorator={
            <IconButton variant="plain" color="danger" onClick={() => setUiError(null)}>
              <CancelIcon />
            </IconButton>
          }
        >
          {uiError}
        </Alert>
      )}

      <Summary />
      {steps.map(
        (step, index) =>
          currentStep === index + 1 && (
            <div key={index} className="flex flex-col space-y-4">
              {step.component ? <step.component /> : <span>{step.title}</span>}
            </div>
          ),
      )}

      <div className="flex items-center justify-end space-x-4">
        {currentStep === steps.length && (
          <>
            <Checkbox
              label={t('openCaseAfterSaving')}
              checked={redirect.toCase}
              onChange={() => setRedirect((prev) => ({ ...prev, toCase: !prev.toCase }))}
            />
            <Checkbox
              label={t('openCaseInNewTabAfterSaving')}
              checked={redirect.toCaseInNewTab}
              onChange={() =>
                setRedirect((prev) => ({
                  ...prev,
                  toCaseInNewTab: !prev.toCaseInNewTab,
                }))
              }
            />
          </>
        )}

        <Button
          variant="outlined"
          startDecorator={<PreviousIcon />}
          disabled={currentStep === 1}
          onClick={() => {
            setUi({
              payload: currentStep - 1,
              type: actions.CASE_STEPPER_SET_REPAIR_CASE_STEP,
            });
            setUiError(null);
          }}
        >
          {t('previous')}
        </Button>

        <Button
          disabled={currentStep > steps.length - 1}
          variant="outlined"
          endDecorator={<NextIcon />}
          onClick={handleNext}
        >
          {t('next')}
        </Button>

        <Button
          disabled={
            steps.length !== currentStep ||
            isSubmitting ||
            (!currentTeam?.allowRepairManagement && !values.sendTo)
          }
          startDecorator={isSubmitting ? <InlineSpinner /> : <ValidIcon />}
          type="submit"
        >
          {t('createCase')}
        </Button>
      </div>

      {showClientAddressWarning.value && (
        <ConfirmationModal
          onConfirm={handleConfirmMissingClientAddress}
          onClose={handleCloseMissingClientAddress}
          toggle={showClientAddressWarning}
          title={t('repairCaseForm.involvedParties.missingAddressTitle')}
          confirmText={t('yes')}
          declineText={t('no')}
        >
          {t('repairCaseForm.involvedParties.missingAddressBody')}
        </ConfirmationModal>
      )}
    </Form>
  );
};

export default AddRepairCaseFormBody;
